import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

import { BspkButton } from '../button';
import { BspkDivider } from '../divider';

export type ButtonDockType = 'elevated' | 'flat';
export type ButtonDockLayout = 'filled' | 'edge';

/**
 * The button dock renders a primary button and an optional secondary button.
 *
 * To render a custom primary button, render a `<button bspk-button variant="primary">` as a child of the button dock.
 * To render a custom secondary button, render a `<button bspk-button variant="secondary">` as a child of the button dock.
 *
 * @example Render a button dock with primary and secondary actions.
 * <bspk-button-dock primaryLabel="Primary Action"
 *                   secondaryLabel="Secondary Action"
 *                   (primaryClick)="onPrimaryClick($event)"
 *                   (secondaryClick)="onSecondaryClick($event)">
 *
 * @example Render a button dock with custom buttons.
 * <bspk-button-dock primaryLabel="Primary Action">
 *   <button bspk-button variant="primary" [routerLink]="['/primary-action']">
 *   <button bspk-button variant="secondary" destructive (click)="doSomethingDestructive()>
 * </bspk-button-dock>
 *
 */
@Component({
  selector: 'bspk-button-dock',
  imports: [BspkButton, BspkDivider],
  templateUrl: './button-dock.component.html',
  styleUrl: './button-dock.component.scss',
  host: {
    'data-bspk': 'button-dock',
    '[attr.data-variant]': 'variant()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BspkButtonDock {
  readonly variant = input<ButtonDockType>('elevated');
  readonly layout = input<ButtonDockLayout>('filled');
  readonly primaryLabel = input();
  readonly secondaryLabel = input<string>();
  readonly primaryClick = output<MouseEvent>();
  readonly secondaryClick = output<MouseEvent>();
}
